import React, { useEffect, useMemo, useState } from 'react';
import {
    CheckCircle2,
    Circle,
    Flame,
    NotebookPen,
    Shield,
    Sparkles,
    MapPin,
    Target,
    Wallet2,
} from 'lucide-react';
import { FireConfig, FireStyle, IncomeStream, Liability, ManualAsset, FireCountry } from '../types';
import { buildCanonicalNetWorth } from '../utils/netWorth';

interface OnboardingWizardProps {
    currentStep: number;
    totalSteps?: number;
    onAdvance: () => void;
    onComplete: () => void;
    onSkip: () => void;
    isComplete?: boolean;
    onClose?: () => void;
    config: FireConfig;
    onUpdateConfig: (config: FireConfig) => void;
    currencySymbol: string;
    portfolioValue: number;
}

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
    currentStep,
    totalSteps = 8,
    onAdvance,
    onComplete,
    onSkip,
    isComplete = false,
    onClose,
    config,
    onUpdateConfig,
    currencySymbol,
    portfolioValue,
}) => {
    const [draftConfig, setDraftConfig] = useState<FireConfig>(config);

    useEffect(() => {
        setDraftConfig(config);
    }, [config]);

    const updateConfig = (nextConfig: FireConfig) => {
        setDraftConfig(nextConfig);
        onUpdateConfig(nextConfig);
    };

    const handleAddAsset = () => {
        const newAsset: ManualAsset = {
            id: Date.now().toString(),
            name: '',
            value: 0,
            growthRate: 5,
            type: 'Cash',
        };
        updateConfig({ ...draftConfig, manualAssets: [...draftConfig.manualAssets, newAsset] });
    };

    const handleAddIncome = () => {
        const newIncome: IncomeStream = {
            id: Date.now().toString(),
            name: 'Salary',
            amount: 0,
            type: 'Salary',
            startAge: draftConfig.currentAge,
            endAge: draftConfig.retirementAge,
        };
        updateConfig({ ...draftConfig, incomeStreams: [...draftConfig.incomeStreams, newIncome] });
    };

    const handleAddLiability = () => {
        const newLiability: Liability = {
            id: Date.now().toString(),
            name: '',
            balance: 0,
            interestRate: 4,
            monthlyPayment: 0,
        };
        updateConfig({ ...draftConfig, liabilities: [...draftConfig.liabilities, newLiability] });
    };

    const steps = useMemo(
        () =>
            [
                {
                    title: 'Vision & goals',
                    description: 'Share your target age, lifestyle and FIRE style.',
                    icon: <Sparkles size={18} />,
                },
                {
                    title: 'Country & regulation',
                    description: 'Tell us where you live so future recommendations align with local rules and compliance.',
                    icon: <MapPin size={18} />,
                },
                {
                    title: 'Assets',
                    description: 'List property, cash, pensions, and other holdings.',
                    icon: <Wallet2 size={18} />,
                },
                {
                    title: 'Income streams',
                    description: 'Add salaries, rentals, side hustles, pensions, and annuities.',
                    icon: <Shield size={18} />,
                },
                {
                    title: 'Expenses',
                    description: 'Define your current and retirement spending levels.',
                    icon: <NotebookPen size={18} />,
                },
                {
                    title: 'Liabilities',
                    description: 'Capture mortgages, loans, and other debts.',
                    icon: <Target size={18} />,
                },
                {
                    title: 'Assumptions',
                    description: 'Set return, inflation, income growth, and withdrawal rates.',
                    icon: <Flame size={18} />,
                },
                {
                    title: 'Review & launch',
                    description: 'Confirm your numbers before entering the dashboard.',
                    icon: <Shield size={18} />,
                },
            ].map((step, index) => {
                const stepNumber = index + 1;
                return {
                    ...step,
                    stepNumber,
                    status: isComplete || stepNumber < currentStep ? 'done' : stepNumber === currentStep ? 'active' : 'pending',
                } as const;
            }),
        [currentStep, isComplete]
    );

    const progress = isComplete
        ? 100
        : Math.min(100, Math.round(((currentStep - 1) / totalSteps) * 100));

    const calculateNetWorth = () => buildCanonicalNetWorth(draftConfig, portfolioValue).total;

    const calculateFireNumber = () => {
        const expenses = draftConfig.retirementExpenses || draftConfig.annualExpenses;
        const rate = draftConfig.withdrawalRate / 100;
        if (!rate) return 0;
        return expenses / rate;
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div className="space-y-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="space-y-2 text-sm text-slate-200">
                                <span className="text-slate-400">Current age</span>
                                <input
                                    type="number"
                                    className="w-full bg-[#0f0f15] border border-white/10 rounded-xl px-3 py-2 text-white"
                                    value={draftConfig.currentAge}
                                    onChange={(e) => updateConfig({ ...draftConfig, currentAge: Number(e.target.value) || 0 })}
                                />
                            </label>
                            <label className="space-y-2 text-sm text-slate-200">
                                <span className="text-slate-400">Target retirement age</span>
                                <input
                                    type="number"
                                    className="w-full bg-[#0f0f15] border border-white/10 rounded-xl px-3 py-2 text-white"
                                    value={draftConfig.retirementAge}
                                    onChange={(e) => updateConfig({ ...draftConfig, retirementAge: Number(e.target.value) || 0 })}
                                />
                            </label>
                            <label className="space-y-2 text-sm text-slate-200">
                                <span className="text-slate-400">Life expectancy</span>
                                <input
                                    type="number"
                                    className="w-full bg-[#0f0f15] border border-white/10 rounded-xl px-3 py-2 text-white"
                                    value={draftConfig.lifeExpectancy}
                                    onChange={(e) => updateConfig({ ...draftConfig, lifeExpectancy: Number(e.target.value) || 0 })}
                                />
                            </label>
                            <label className="space-y-2 text-sm text-slate-200">
                                <span className="text-slate-400">FIRE style</span>
                                <select
                                    className="w-full bg-[#0f0f15] border border-white/10 rounded-xl px-3 py-2 text-white"
                                    value={draftConfig.fireStyle}
                                    onChange={(e) => updateConfig({ ...draftConfig, fireStyle: e.target.value as FireStyle })}
                                >
                                    <option value="Lean">Lean</option>
                                    <option value="Traditional">Traditional</option>
                                    <option value="Fat">Fat</option>
                                    <option value="Coast">Coast</option>
                                    <option value="Barista">Barista</option>
                                </select>
                            </label>
                        </div>

                        <label className="space-y-2 text-sm text-slate-200 block">
                            <span className="text-slate-400">Why FIRE matters to you</span>
                            <textarea
                                className="w-full bg-[#0f0f15] border border-white/10 rounded-xl px-3 py-2 text-white min-h-[90px]"
                                value={draftConfig.lifestyleGoals}
                                onChange={(e) => updateConfig({ ...draftConfig, lifestyleGoals: e.target.value })}
                                placeholder="Describe the lifestyle, freedom, or impact you're aiming for."
                            />
                        </label>
                    </div>
                );
            case 2:
                return (
                    <div className="space-y-4">
                        <label className="space-y-2 text-sm text-slate-200 block">
                            <span className="text-slate-400">Country / region</span>
                            <select
                                className="w-full bg-[#0f0f15] border border-white/10 rounded-xl px-3 py-2 text-white"
                                value={draftConfig.country || ''}
                                onChange={(e) => updateConfig({ ...draftConfig, country: e.target.value as FireCountry })}
                            >
                                <option value="" disabled>Select your country</option>
                                <option value="UK">UK</option>
                                <option value="US">US</option>
                                <option value="Canada">Canada</option>
                                <option value="EU">EU</option>
                                <option value="Other">Other</option>
                            </select>
                        </label>
                        <div className="rounded-xl border border-indigo-500/30 bg-indigo-500/10 px-4 py-3 text-sm text-indigo-100">
                            We use your country to tailor future wealth guidance around local regulations, tax wrappers, and compliance expectations.
                        </div>
                    </div>
                );
            case 3:
                return (
                    <div className="space-y-4">
                        {draftConfig.manualAssets.map((asset, idx) => (
                            <div
                                key={asset.id}
                                className="flex flex-col md:flex-row md:items-center gap-3 bg-[#0f0f15] border border-white/5 rounded-xl p-3"
                            >
                                <input
                                    type="text"
                                    value={asset.name}
                                    placeholder="Asset name (e.g. Home, Cash)"
                                    onChange={(e) => {
                                        const nextAssets = [...draftConfig.manualAssets];
                                        nextAssets[idx] = { ...asset, name: e.target.value };
                                        updateConfig({ ...draftConfig, manualAssets: nextAssets });
                                    }}
                                    className="flex-1 bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white"
                                />
                                <select
                                    value={asset.type}
                                    onChange={(e) => {
                                        const nextAssets = [...draftConfig.manualAssets];
                                        nextAssets[idx] = { ...asset, type: e.target.value as ManualAsset['type'] };
                                        updateConfig({ ...draftConfig, manualAssets: nextAssets });
                                    }}
                                    className="md:w-40 bg-[#1b1b24] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                                >
                                    <option value="Real Estate">Real Estate</option>
                                    <option value="Cash">Cash</option>
                                    <option value="Crypto">Crypto</option>
                                    <option value="ISA">ISA</option>
                                    <option value="GIA">GIA</option>
                                    <option value="Pension">Pension Pot (DC / Workplace)</option>
                                    <option value="Other">Other</option>
                                </select>
                                <div className="relative md:w-40">
                                    <span className="absolute left-3 top-2 text-slate-500 text-xs">{currencySymbol}</span>
                                    <input
                                        type="number"
                                        value={asset.value}
                                        onChange={(e) => {
                                            const nextAssets = [...draftConfig.manualAssets];
                                            nextAssets[idx] = { ...asset, value: Number(e.target.value) || 0 };
                                            updateConfig({ ...draftConfig, manualAssets: nextAssets });
                                        }}
                                        className="w-full bg-[#1b1b24] border border-white/10 rounded-lg pl-7 pr-3 py-2 text-right text-white"
                                    />
                                </div>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={handleAddAsset}
                            className="w-full py-3 border border-dashed border-white/20 rounded-xl text-slate-300 hover:text-white hover:border-white/40"
                        >
                            + Add asset
                        </button>
                    </div>
                );
            case 4:
                return (
                    <div className="space-y-4">
                        {draftConfig.incomeStreams.map((stream, idx) => (
                            <div
                                key={stream.id}
                                className="grid grid-cols-1 md:grid-cols-12 gap-3 bg-[#0f0f15] border border-white/5 rounded-xl p-3"
                            >
                                <input
                                    type="text"
                                    value={stream.name}
                                    onChange={(e) => {
                                        const next = [...draftConfig.incomeStreams];
                                        next[idx] = { ...stream, name: e.target.value };
                                        updateConfig({ ...draftConfig, incomeStreams: next });
                                    }}
                                    placeholder="Income name"
                                    className="md:col-span-3 bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white"
                                />
                                <select
                                    value={stream.type}
                                    onChange={(e) => {
                                        const next = [...draftConfig.incomeStreams];
                                        next[idx] = { ...stream, type: e.target.value as IncomeStream['type'] };
                                        updateConfig({ ...draftConfig, incomeStreams: next });
                                    }}
                                    className="md:col-span-3 bg-[#1b1b24] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                                >
                                    <option value="Salary">Salary</option>
                                    <option value="Side-Hustle">Side-Hustle</option>
                                    <option value="Rental">Rental</option>
                                    <option value="Pension">DB / State Pension</option>
                                    <option value="Annuity">Annuity</option>
                                    <option value="Other">Other</option>
                                </select>
                                <div className="md:col-span-3 relative">
                                    <span className="absolute left-3 top-2 text-slate-500 text-xs">{currencySymbol}</span>
                                    <input
                                        type="number"
                                        value={stream.amount}
                                        onChange={(e) => {
                                            const next = [...draftConfig.incomeStreams];
                                            next[idx] = { ...stream, amount: Number(e.target.value) || 0 };
                                            updateConfig({ ...draftConfig, incomeStreams: next });
                                        }}
                                        className="w-full bg-[#1b1b24] border border-white/10 rounded-lg pl-7 pr-3 py-2 text-right text-white"
                                    />
                                </div>
                                <div className="md:col-span-3 grid grid-cols-2 gap-2">
                                    <input
                                        type="number"
                                        value={stream.startAge}
                                        onChange={(e) => {
                                            const next = [...draftConfig.incomeStreams];
                                            next[idx] = { ...stream, startAge: Number(e.target.value) || 0 };
                                            updateConfig({ ...draftConfig, incomeStreams: next });
                                        }}
                                        className="bg-[#1b1b24] border border-white/10 rounded-lg px-3 py-2 text-center text-white"
                                        placeholder="Start age"
                                    />
                                    <input
                                        type="number"
                                        value={stream.endAge}
                                        onChange={(e) => {
                                            const next = [...draftConfig.incomeStreams];
                                            next[idx] = { ...stream, endAge: Number(e.target.value) || 0 };
                                            updateConfig({ ...draftConfig, incomeStreams: next });
                                        }}
                                        className="bg-[#1b1b24] border border-white/10 rounded-lg px-3 py-2 text-center text-white"
                                        placeholder="End age"
                                    />
                                </div>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={handleAddIncome}
                            className="w-full py-3 border border-dashed border-white/20 rounded-xl text-slate-300 hover:text-white hover:border-white/40"
                        >
                            + Add income stream
                        </button>
                    </div>
                );
            case 5:
                return (
                    <div className="space-y-5">
                        <label className="space-y-2 text-sm text-slate-200 block">
                            <span className="text-slate-400">Current annual spending</span>
                            <div className="relative">
                                <span className="absolute left-3 top-2.5 text-slate-500">{currencySymbol}</span>
                                <input
                                    type="number"
                                    value={draftConfig.annualExpenses}
                                    onChange={(e) => updateConfig({ ...draftConfig, annualExpenses: Number(e.target.value) || 0 })}
                                    className="w-full bg-[#0f0f15] border border-white/10 rounded-xl pl-8 pr-3 py-3 text-white"
                                />
                            </div>
                        </label>
                        <label className="space-y-2 text-sm text-slate-200 block">
                            <span className="text-slate-400">Target retirement spending</span>
                            <div className="relative">
                                <span className="absolute left-3 top-2.5 text-emerald-500/80">{currencySymbol}</span>
                                <input
                                    type="number"
                                    value={draftConfig.retirementExpenses || draftConfig.annualExpenses}
                                    onChange={(e) =>
                                        updateConfig({ ...draftConfig, retirementExpenses: Number(e.target.value) || 0 })
                                    }
                                    className="w-full bg-[#0f0f15] border border-emerald-500/30 rounded-xl pl-8 pr-3 py-3 text-white"
                                />
                            </div>
                        </label>
                    </div>
                );
            case 6:
                return (
                    <div className="space-y-4">
                        {draftConfig.liabilities.map((liab, idx) => (
                            <div
                                key={liab.id}
                                className="flex flex-col md:flex-row md:items-center gap-3 bg-[#0f0f15] border border-white/5 rounded-xl p-3"
                            >
                                <input
                                    type="text"
                                    value={liab.name}
                                    placeholder="Debt name (e.g. Mortgage)"
                                    onChange={(e) => {
                                        const next = [...draftConfig.liabilities];
                                        next[idx] = { ...liab, name: e.target.value };
                                        updateConfig({ ...draftConfig, liabilities: next });
                                    }}
                                    className="flex-1 bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white"
                                />
                                <div className="relative md:w-40">
                                    <span className="absolute left-3 top-2 text-slate-500 text-xs">{currencySymbol}</span>
                                    <input
                                        type="number"
                                        value={liab.balance}
                                        onChange={(e) => {
                                            const next = [...draftConfig.liabilities];
                                            next[idx] = { ...liab, balance: Number(e.target.value) || 0 };
                                            updateConfig({ ...draftConfig, liabilities: next });
                                        }}
                                        className="w-full bg-[#1b1b24] border border-white/10 rounded-lg pl-7 pr-3 py-2 text-right text-white"
                                    />
                                </div>
                                <div className="md:w-28 flex items-center gap-2">
                                    <input
                                        type="number"
                                        value={liab.interestRate}
                                        onChange={(e) => {
                                            const next = [...draftConfig.liabilities];
                                            next[idx] = { ...liab, interestRate: Number(e.target.value) || 0 };
                                            updateConfig({ ...draftConfig, liabilities: next });
                                        }}
                                        className="w-full bg-[#1b1b24] border border-white/10 rounded-lg px-3 py-2 text-center text-white"
                                    />
                                    <span className="text-slate-500 text-xs">%</span>
                                </div>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={handleAddLiability}
                            className="w-full py-3 border border-dashed border-white/20 rounded-xl text-slate-300 hover:text-white hover:border-white/40"
                        >
                            + Add liability
                        </button>
                    </div>
                );
            case 7:
                return (
                    <div className="space-y-6">
                        <div>
                            <div className="flex justify-between text-sm text-slate-200 mb-2">
                                <span className="text-slate-400">Investment return rate</span>
                                <span className="font-semibold">{draftConfig.investmentReturnRate}%</span>
                            </div>
                            <input
                                type="range"
                                min="2"
                                max="12"
                                step="0.5"
                                value={draftConfig.investmentReturnRate}
                                onChange={(e) => updateConfig({ ...draftConfig, investmentReturnRate: Number(e.target.value) })}
                                className="w-full accent-emerald-500"
                            />
                        </div>
                        <div>
                            <div className="flex justify-between text-sm text-slate-200 mb-2">
                                <span className="text-slate-400">Safe withdrawal rate</span>
                                <span className="font-semibold">{draftConfig.withdrawalRate}%</span>
                            </div>
                            <input
                                type="range"
                                min="2"
                                max="6"
                                step="0.1"
                                value={draftConfig.withdrawalRate}
                                onChange={(e) => updateConfig({ ...draftConfig, withdrawalRate: Number(e.target.value) })}
                                className="w-full accent-indigo-500"
                            />
                        </div>
                        <div>
                            <div className="flex justify-between text-sm text-slate-200 mb-2">
                                <span className="text-slate-400">Inflation rate</span>
                                <span className="font-semibold">{draftConfig.inflationRate}%</span>
                            </div>
                            <input
                                type="range"
                                min="1"
                                max="8"
                                step="0.1"
                                value={draftConfig.inflationRate}
                                onChange={(e) => updateConfig({ ...draftConfig, inflationRate: Number(e.target.value) })}
                                className="w-full accent-rose-500"
                            />
                        </div>
                        <div>
                            <div className="flex justify-between text-sm text-slate-200 mb-2">
                                <span className="text-slate-400">Income growth rate</span>
                                <span className="font-semibold">{draftConfig.incomeGrowthRate}%</span>
                            </div>
                            <input
                                type="range"
                                min="0"
                                max="8"
                                step="0.1"
                                value={draftConfig.incomeGrowthRate}
                                onChange={(e) => updateConfig({ ...draftConfig, incomeGrowthRate: Number(e.target.value) })}
                                className="w-full accent-sky-500"
                            />
                        </div>
                    </div>
                );
            default: {
                const netWorth = calculateNetWorth();
                const fireNumber = calculateFireNumber();
                const progressToFire = fireNumber > 0 ? Math.min(100, Math.max(0, (netWorth / fireNumber) * 100)) : 0;
                return (
                    <div className="space-y-4 text-slate-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-[#0f0f15] border border-white/5 rounded-xl p-4">
                                <div className="text-xs uppercase text-slate-500">Net worth</div>
                                <div className="text-2xl font-bold text-white mt-1">
                                    {currencySymbol}
                                    {netWorth.toLocaleString()}
                                </div>
                            </div>
                            <div className="bg-[#0f0f15] border border-white/5 rounded-xl p-4">
                                <div className="text-xs uppercase text-slate-500">FIRE target</div>
                                <div className="text-2xl font-bold text-emerald-400 mt-1">
                                    {currencySymbol}
                                    {Math.round(fireNumber).toLocaleString()}
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="flex justify-between text-xs mb-2 text-slate-400">
                                <span>Progress toward FIRE</span>
                                <span className="text-white font-semibold">{progressToFire.toFixed(1)}%</span>
                            </div>
                            <div className="w-full bg-[#0f0f15] border border-white/5 h-3 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-emerald-500"
                                    style={{ width: `${progressToFire}%` }}
                                />
                            </div>
                        </div>
                        <p className="text-sm text-slate-400">
                            You can refine any step later from the FIRE center. Finishing onboarding will save these numbers as your new baseline.
                        </p>
                    </div>
                );
            }
        }
    };

    return (
        <div className="bg-[#1e1e26] rounded-2xl border border-white/5 shadow-lg p-6 animate-in fade-in slide-in-from-bottom-4 duration-500 relative">
            {onClose && (
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-4 right-4 text-slate-500 hover:text-white"
                    aria-label="Close onboarding"
                >
                    ×
                </button>
            )}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div className="space-y-1">
                    <div className="inline-flex items-center px-2 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/30 text-xs font-semibold text-indigo-300 uppercase tracking-wide">
                        <Flame size={14} className="mr-1" />
                        FIRE onboarding
                    </div>
                    <h2 className="text-xl font-bold text-white">Finish setting up your FIRE command center</h2>
                    <p className="text-sm text-slate-400">
                        We guide you through an 8-step checklist to personalize projections and unlock the dashboard.
                    </p>
                </div>
                <div className="text-right space-y-2 min-w-[180px]">
                    <div className="flex items-center justify-between text-xs text-slate-400">
                        <span>
                            Step {Math.min(currentStep, totalSteps)} of {totalSteps}
                        </span>
                        <span className="font-semibold text-white">{progress}%</span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-[#0f0f15] overflow-hidden border border-white/5">
                        <div className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-emerald-500" style={{ width: `${progress}%` }} />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
                {steps.map((step) => (
                    <div
                        key={step.title}
                        className={`relative p-4 rounded-xl border transition-all duration-200 bg-[#13131a] shadow-sm hover:-translate-y-0.5 hover:shadow-xl hover:shadow-indigo-500/20 ${
                            step.status === 'done'
                                ? 'border-emerald-500/40'
                                : step.status === 'active'
                                ? 'border-indigo-500/50'
                                : 'border-white/5'
                        }`}
                    >
                        <div className="flex items-start justify-between">
                            <div className="flex items-center space-x-3">
                                <div
                                    className={`w-9 h-9 rounded-full flex items-center justify-center text-white ${
                                        step.status === 'done'
                                            ? 'bg-emerald-500/10 border border-emerald-500/40'
                                            : step.status === 'active'
                                            ? 'bg-indigo-500/10 border border-indigo-500/40'
                                            : 'bg-white/5 border border-white/10'
                                    }`}
                                >
                                    {step.status === 'done' ? <CheckCircle2 size={18} className="text-emerald-400" /> : step.icon}
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-wide text-slate-500">Step {step.stepNumber}</div>
                                    <div className="text-white font-semibold">{step.title}</div>
                                </div>
                            </div>
                            {step.status === 'active' ? (
                                <span className="text-[10px] px-2 py-1 rounded-full bg-indigo-500/10 text-indigo-300 border border-indigo-500/30 font-semibold uppercase tracking-wide">
                                    In progress
                                </span>
                            ) : step.status === 'done' ? (
                                <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 font-semibold uppercase tracking-wide">
                                    Complete
                                </span>
                            ) : (
                                <Circle size={14} className="text-slate-600" />
                            )}
                        </div>
                        <p className="text-sm text-slate-400 mt-3 leading-relaxed">{step.description}</p>
                    </div>
                ))}
            </div>

            <div className="bg-[#0f0f15] border border-white/5 rounded-2xl p-5 mb-6">
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-9 h-9 bg-indigo-500/10 border border-indigo-500/40 rounded-full flex items-center justify-center text-indigo-200">
                        <NotebookPen size={16} />
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wide text-slate-500">Step {Math.min(currentStep, totalSteps)}</div>
                        <div className="text-white font-semibold">{steps[Math.min(currentStep, totalSteps) - 1]?.title}</div>
                    </div>
                </div>
                {renderStepContent()}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="text-sm text-slate-400">
                    Your FIRE tiles will light up as you finish each step. You can revisit any step later from the FIRE center.
                </div>
                <div className="flex items-center space-x-3">
                    <button
                        className="px-4 py-2 rounded-lg text-sm font-medium text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                        onClick={() => {
                            onSkip();
                            onClose?.();
                        }}
                        type="button"
                    >
                        Skip for now
                    </button>
                    <button
                        className="px-4 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/20 hover:from-indigo-500 hover:to-purple-500 transition-colors"
                        onClick={() => {
                            if (isComplete || currentStep >= totalSteps) {
                                onComplete();
                                onClose?.();
                            } else {
                                onAdvance();
                            }
                        }}
                        type="button"
                    >
                        {isComplete || currentStep >= totalSteps ? 'Finish onboarding' : 'Continue setup'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OnboardingWizard;
